import React, { memo, ReactNode } from 'react';
// Button.jsx
import classNames from 'classnames';
import StyleComponentProps from 'core/utils/types/StyleComponentProps';
import { ButtonType } from 'core/utils/types/ComponentTypes';
import { ifNotPreviousYear } from 'core/utils/constants';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { useSelector } from 'react-redux';
import { RootState } from 'controller/types';
import { SetupLoader } from '../SetupComponents/SetupLoader';

interface Props extends StyleComponentProps {
  type: string;
  size?: string;
  label?: string;
  icon?: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
  active?: boolean;
  labelClasses?: string;
  index?: number;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  ref?: React.RefObject<HTMLButtonElement>;
  id?: string;

  // functional types
  submit?: boolean;
  button?: boolean;
  isPreviousYearCheckNotRequied?: boolean;
  wantPreviousYearValidation?: boolean;
  activateLoader?: boolean;
  isLoading?: boolean;
}

const checkDisablity = (data, isTrue) => {
  if (data.disabled) {
    return true;
  } else if ((data.submit || data?.wantPreviousYearValidation) && !data?.isPreviousYearCheckNotRequied && !isTrue) {
    return true;
  }
  return false;
};

export const Button = memo((props: Props) => {
  const authData = useAuth();
  const isPrimary = () => props.type === ButtonType.primary;
  const isOutlined = () => props.type === ButtonType.outlined;
  const isWhite = () => props.type === ButtonType.white;
  const isGhost = () => props.type === ButtonType.ghost;

  const branchState: any = useSelector((state: RootState) => state.branchSetup);
  const admissionState: any = useSelector((state: RootState) => state.admissionSetup);
  const userState: any = useSelector((state: RootState) => state.userSetup);
  const classState: any = useSelector((state: RootState) => state.classSetup);
  const communicationState: any = useSelector((state: RootState) => state.communicationSetup);
  const examState: any = useSelector((state: RootState) => state.examSetup);
  const feeState: any = useSelector((state: RootState) => state.feeSetup);
  const transportState: any = useSelector((state: RootState) => state.transportSetup);
  const supportTicket: any = useSelector((state: RootState) => state.supportTicket);
  const socialHubState: any = useSelector((state: RootState) => state.socialHub);

  const isLoding = (branchState?.isUpsertLoading || classState?.isUpsertLoading || admissionState?.isUpsertLoading || userState?.isUpsertLoading ||
     transportState?.isUpsertLoading || feeState?.isUpsertLoading || examState?.isUpsertLoading || communicationState?.isUpsertLoading || 
     supportTicket?.isCreatingTicket || socialHubState?.isUpsertLoading);

  const renderLoader = () => {
    if (props?.isLoading) {
      return true;
    }

    if(props?.submit || props?.activateLoader) {
      return isLoding;
    }
    return false;
  }

  return (
    <button
      type={props.submit ? 'submit' : props.button ? 'button' : undefined}
      onClick={props.onClick}
      disabled={(checkDisablity({ ...props }, ifNotPreviousYear(authData)) || renderLoader())}
      className={classNames(
        `!min-w-max font-bold ${props.padding ? props.padding : 'py-2 px-4'
        } rounded-[6px] ${props.margin} ${props.width ? props.width : 'w-[100px]'
        } ${props.height ? props.height : 'h-[40px]'}
          flex ${props.icon ? 'justify-start' : 'justify-center'} items-center
        `,
        {
          /* Backgound Colors */
          'bg-primary-main': isPrimary(),
          'hover:bg-primary-70':
            isPrimary() &&
            !checkDisablity({ ...props }, ifNotPreviousYear(authData)) 
            ,
          'bg-primary-30':
            isPrimary() &&
            (checkDisablity({ ...props }, ifNotPreviousYear(authData)) ),

          'bg-neutral-light':
            isWhite() ||
            isOutlined() ||
            (isGhost() &&
              !checkDisablity({ ...props }, ifNotPreviousYear(authData)) &&
              props.active),
          'hover:bg-primary-10':
            (isWhite() || isGhost() || isOutlined()) &&
            !checkDisablity({ ...props }, ifNotPreviousYear(authData)) &&
            !props.active,

          /* Text Colors */
          'text-neutral-light': isPrimary(),
          'text-primary-main':
            isWhite() ||
            isOutlined() ||
            (isGhost() &&
              !checkDisablity({ ...props }, ifNotPreviousYear(authData))),

          'text-primary-40':
            !isPrimary() &&
            (checkDisablity({ ...props }, ifNotPreviousYear(authData)) ),

          /* Border Colors */
          'border-primary-main': isOutlined(),
          'border-primary-40':
            isOutlined() &&
            (checkDisablity({ ...props }, ifNotPreviousYear(authData))),
          'border-[1px]': isOutlined(),
          'border-solid': isOutlined(),
          'cursor-not-allowed': checkDisablity(
            { ...props },
            ifNotPreviousYear(authData),
          ),
        },
        props.className,
      )}
    >
      {props.icon && props.icon}
      {props.label && (
        <span
          className={classNames('inline-block', props.labelClasses, {
            'text-xl': props.size === 'large',
            'text-lg': props.size === 'medium',
            'text-base': props.size === 'small',
          })}
        >
          {renderLoader() ? <span data-testid="button-loader" className="inline-block bg-neutral-light p-[2px] rounded-full"><SetupLoader height='h-10' /></span> : props.label}
        </span>
      )}
    </button>
  );
});
